import styled from 'styled-components';
import { Colors } from 'basic-ui-lib/constants';
import { Text } from 'basic-ui-lib/components/Text';
import { IcCheck } from 'basic-ui-lib/icons';
import { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { LayoutCode } from 'modules/channel/services/constants';
import MainZone from 'basic-ui-lib/components/screen/MainZone';
import Portrait from 'basic-ui-lib/components/screen/Portrait';
import BaseModal from 'basic-ui-lib/components/Modal/BaseModal';
import { useMediaQuery } from 'react-responsive';

export const LAYOUT_CONFIGS = [
  {
    Component: <MainZone border={4} />,
    layoutCode: LayoutCode.MAIN_ONLY,
    name: 'Landscape',
  },
  {
    Component: <Portrait border={4} />,
    layoutCode: LayoutCode.MAIN_PORTRAIT,
    name: 'Portrait',
  },
];
type Props = {};

type Ref = {
  show: (currentLayout: LayoutCode, onConfirm: (layout: LayoutCode) => void) => void;
  hide: () => void;
};

export type ChangeLayoutModalRef = Ref;

export const useChangeLayoutModalRef = () => useRef<Ref>(null);

const ChangeLayoutModal = forwardRef<Ref, Props>(({}, ref) => {
  const [open, setOpen] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<LayoutCode>(LayoutCode.MAIN_ONLY);
  const onConfirmRef = useRef<(layout: LayoutCode) => void>(() => undefined);

  const isMobile = useMediaQuery({ maxWidth: 768 });
  
  const onShow: Ref['show'] = (currentLayout, onConfirm) => {
    setSelectedLayout(currentLayout);
    onConfirmRef.current = onConfirm;
    setOpen(true);
  };

  const onHide: Ref['hide'] = () => {
    setOpen(false);
  };

  useImperativeHandle(ref, () => ({
    show: onShow,
    hide: onHide,
  }));

  const handleConfirm = () => {
    onConfirmRef.current(selectedLayout);
    onHide();
  };

  const handleClose = () => {
    onHide();
  };

  return (
    <BaseModal
      title='Change Layout'
      isOpen={open}
      width={900}
      onClose={handleClose}
      okText='OK'
      onOk={handleConfirm}
      isMobile={isMobile}
    >
      <ModalContent>
          <Content>
            {LAYOUT_CONFIGS.map((layout) => (
              <ItemLayout
                key={layout.layoutCode}
                className={selectedLayout === layout.layoutCode ? 'active' : ''}
                onClick={() => setSelectedLayout(layout.layoutCode)}
              >
                {layout.Component}
                {
                  selectedLayout === layout.layoutCode && (
                    <BoxIcon>
                      <IcCheck width={40} height={40} fill={Colors.secondary[5]}/>
                    </BoxIcon>
                  )
                }
                <Text content_2 medium style={{ paddingBottom: 8 }}>
                  {layout.name}
                </Text>
              </ItemLayout>
            ))}
          </Content>
      </ModalContent>
    </BaseModal>
  );
});

ChangeLayoutModal.displayName = 'ChangeLayoutModal';

export default ChangeLayoutModal;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 90px 40px;
  height: 255px;

  @media (max-width: 768px) {
    padding: 16px;
    height: 180px;
  }
`;

const Content = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, 1fr);
  padding: 0 55px;

  @media (max-width: 768px) {
    padding: 0;
    gap: 8px;
  }
`;

const ItemLayout = styled.div`
  display: flex;
  padding: 4px;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  position: relative;
  cursor: pointer;

  border-radius: 4px;
  border: 1px solid ${Colors.neutral.border[1]};
  background: ${Colors.neutral.bg[1]};
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.15);

  &.active {
    border: 2px solid ${Colors.primary[6]};
  }
`;

const BoxIcon = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
`;